import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import {
  assign,
  each,
  every,
  isArray,
  isFunction,
  isObject,
  isUndefined,
  sortBy
} from 'lodash';
import { Subject } from 'rxjs';
import { CompanyVendor, CompanyVendorPayment } from 'src/app/classes/models';
import { ClientVendorLabelEntity } from 'src/app/interfaces/client-vendor-label-entity';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { RegistrationProgress } from 'src/app/interfaces/models/registration-progress';
import { RegistrationStep } from 'src/app/interfaces/models/registration-step';
import { RegistrationStepName } from 'src/app/interfaces/registration-step-name';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { RegistrationService } from 'src/app/services/registration.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-registration-tracker',
  templateUrl: './registration-tracker.component.html',
  styleUrls: ['./registration-tracker.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RegistrationTrackerComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);
  constructor(
    private router: Router,
    private RegistrationService: RegistrationService,
    private CompanyVendorService: CompanyVendorService,
    private ToastService: ToastService
  ) {}

  RegistrationSteps: RegistrationStep[] = [];

  // This map will have the RegistrationStep id as a key and the step as a value. Uses a little more memory
  // to declare the map and the array (above), however, the map will allow for quick updates to the Registration
  // Step records by access through id.
  RegistrationStepMap: { [key: number]: RegistrationStep } = {};

  // A Registration Step can have multiple progress records now (8/4/2023)
  // Since this is the case, we need a way to map the Step Id to a map containing either
  // CompanyId or VendorId => RegistrationProgress.
  RegistrationStepToProgressMap: {
    [key: number]: { [key: number]: RegistrationProgress };
  } = {};

  @Input() CompanyIds: number[] = [];
  @Input() VendorId?: number;
  @Input() BundleId?: number;
  @Input() CompanyVendorId?: number;
  @Input() registrationType!: 'Client' | 'Vendor' | 'CompanyVendor';
  @Input() paymentType!: 'registration' | 'renewal';
  @Input() RecentCompanyVendorPaymentsMappedByCompanyVendorId = new Map<
    number,
    CompanyVendorPayment
  >();
  @Input() disableView: boolean = false;

  // Fires event when an entity is selected from the drop down.
  @Output() selectedEntityEvent: EventEmitter<ClientVendorLabelEntity> =
    new EventEmitter();
  // Represents selectable 'entities' (Companies/Vendors) at the top of the Registration Tracker.
  entitySelectionCollection: ClientVendorLabelEntity[] = [];
  // Represents the selected entity. This allows for the correct registration progress(es) to be loaded.
  selectedEntity?: ClientVendorLabelEntity;

  // Represents the most recent client/vendor payment (made by a Vendor) for the current selected entity (Client).
  selectedRecentCompanyVendorPayment?: CompanyVendorPayment;
  // Tracks the feeWaived status of the currently selected entity's Client/Vendor relationship.
  selectedCompanyVendorFeeWaived: boolean = false;
  // Text shown on the payment step, keyed by its resolved state.
  paymentStepStateToTextMap: {
    feeWaived: string;
    paid: string;
    expired: string;
    unpaid: string;
  } = {
    feeWaived: 'Complete - Fee Waived',
    paid: 'Paid in full',
    expired: 'Expired',
    unpaid: 'Unpaid'
  };
  // Maps CompanyVendorId to its feeWaived status so it can be looked up on entity selection.
  CompanyVendorFeeWaivedMappedByCompanyVendorId = new Map<number, boolean>();
  CompanyVendors: CompanyVendor[] = [];

  registrationTypeToSelectionTypeMap: {
    Client: 'CompanyId';
    Vendor: 'VendorId';
    CompanyVendor: 'CompanyId';
  } = {
    Client: 'CompanyId',
    Vendor: 'VendorId',
    CompanyVendor: 'CompanyId'
  };

  // Can optionally pass a collection of step names to exclude from being loaded.
  @Input() excludedSteps?: Set<RegistrationStepName>;

  // Steps treated as Complete when no RegistrationProgress row exists
  // (e.g. licenses skipped because no license requirements).
  @Input() forceCompleteSteps?: Set<RegistrationStepName>;

  // Can optionally display a logo at the top of the tracker component.
  @Input() logo?: string | null;

  // An RXJS Subject passed to this component will allow for communication between 2 (or more) components.
  @Input() communicator?: Subject<any>;

  // Used to determine if the Payment proportion of the tracker should be displayed.
  areAllClientVendorRelationshipsFeeWaived: boolean = true;

  registrationStepNameMap = {
    Vendor: [
      'businessInfo',
      'contacts',
      'trades',
      'licenses',
      'owners',
      'insurance',
      'vendorAgreement',
      'finalReview',
      'payment'
    ],
    CompanyVendor: [
      'businessInfo',
      'contacts',
      'trades',
      'licenses',
      'owners',
      'insurance',
      'vendorAgreement',
      'finalReview',
      'payment'
    ],
    Client: []
  };

  // We could attempt to order the data on the backend or we can generate a map (such as here) that maps
  // the name of the step to the correct position in the array.
  // Avery Berninger - 5/3/2023 should implement a merge sort on the backend to sort this data quickly.
  // For now, we can work with this map.
  stepNameToArrayPositionMap: {
    Vendor: {
      businessInfo: 0;
      contacts: 1;
      trades: 2;
      licenses: 3;
      owners: 4;
      insurance: 5;
      vendorAgreement: 6;
      finalReview: 7;
      payment: 8;
    };
    CompanyVendor: {
      businessInfo: 0;
      contacts: 1;
      trades: 2;
      licenses: 3;
      owners: 4;
      insurance: 5;
      vendorAgreement: 6;
      finalReview: 7;
      payment: 8;
    };
    Client: {};
  } = {
    Vendor: {
      businessInfo: 0,
      contacts: 1,
      trades: 2,
      licenses: 3,
      owners: 4,
      insurance: 5,
      vendorAgreement: 6,
      finalReview: 7,
      payment: 8
    },
    CompanyVendor: {
      businessInfo: 0,
      contacts: 1,
      trades: 2,
      licenses: 3,
      owners: 4,
      insurance: 5,
      vendorAgreement: 6,
      finalReview: 7,
      payment: 8
    },
    Client: {}
  };

  // This is used to map the Registration Step name (from the record)
  // to a label-appropriate text.
  stepNameToLabelMap: {
    Vendor: { [key: string]: string };
    CompanyVendor: { [key: string]: string };
    Client: { [key: string]: string };
  } = {
    Vendor: {
      businessInfo: 'Business Information',
      contacts: 'User & Contact Information',
      trades: 'Trade Information',
      licenses: 'License Information',
      owners: 'Owner Information',
      insurance: 'Insurance Information',
      vendorAgreement: 'Vendor Agreement',
      finalReview: 'Final Review',
      payment: 'Active status:'
    },
    CompanyVendor: {
      businessInfo: 'Business Information',
      contacts: 'User & Contact Information',
      trades: 'Trade Information',
      licenses: 'License Information',
      owners: 'Owner Information',
      insurance: 'Insurance Information',
      vendorAgreement: 'Vendor Agreement',
      finalReview: 'Final Review',
      payment: 'Active status:'
    },
    Client: {}
  };

  constructPartialRoutingArrayForVendor(
    {
      CompanyIds,
      BundleId
    }: {
      CompanyIds?: number[];
      BundleId?: number;
    },
    VendorId: number
  ) {
    if (!CompanyIds && !BundleId) {
      throw new Error('Either Client Ids or Bundle Id is required.');
    }

    const navArray: (string | number)[] = ['vendor-registration'];

    if (BundleId) {
      navArray.push('bundle', BundleId);
    } else if (CompanyIds) {
      navArray.push('company', CompanyIds.join(','));
    }

    navArray.push('vendor', VendorId);
    return navArray;
  }

  // Client/Vendor & Vendor share the same registration process. Thus, would be worthwhile to scrap Vendor Registration.
  stepNameToNavigationMap: {
    Vendor: {
      [key: string]: (
        { CompanyIds, BundleId }: { CompanyIds?: number[]; BundleId?: number },
        VendorId: number
      ) => (string | number)[];
    };
    CompanyVendor: {
      [key: string]: (
        { CompanyIds, BundleId }: { CompanyIds?: number[]; BundleId?: number },
        VendorId: number
      ) => (string | number)[];
    };
    Client: { [key: string]: (string | number)[] };
  } = {
    Vendor: {
      businessInfo: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'business-info', 'business-info');
        return navArray;
      },
      contacts: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'business-info', 'contacts');
        return navArray;
      },
      trades: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'trades');
        return navArray;
      },
      licenses: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'licenses');
        return navArray;
      },
      owners: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'owners');
        return navArray;
      },
      insurance: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'insurance');
        return navArray;
      },
      vendorAgreement: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'vendor-agreement');
        return navArray;
      },
      finalReview: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'final-review');
        return navArray;
      },
      payment: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'pay-fees');
        return navArray;
      }
    },
    CompanyVendor: {
      businessInfo: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'business-info', 'business-info');
        return navArray;
      },
      contacts: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'business-info', 'contacts');
        return navArray;
      },
      trades: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'trades');
        return navArray;
      },
      licenses: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'licenses');
        return navArray;
      },
      owners: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'owners');
        return navArray;
      },
      insurance: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'insurance');
        return navArray;
      },
      vendorAgreement: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'vendor-agreement');
        return navArray;
      },
      finalReview: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'final-review');
        return navArray;
      },
      payment: ({ CompanyIds, BundleId }, VendorId) => {
        const navArray = this.constructPartialRoutingArrayForVendor(
          { CompanyIds, BundleId },
          VendorId
        );
        navArray.push(this.paymentType, 'pay-fees');
        return navArray;
      }
    },
    Client: {}
  };

  entitySelection(selectedEntity: ClientVendorLabelEntity) {
    const { CompanyVendorId } = selectedEntity;
    if (CompanyVendorId) {
      this.selectedRecentCompanyVendorPayment =
        this.RecentCompanyVendorPaymentsMappedByCompanyVendorId.get(
          CompanyVendorId
        );
      this.selectedCompanyVendorFeeWaived =
        this.CompanyVendorFeeWaivedMappedByCompanyVendorId.get(
          CompanyVendorId
        ) ?? false;
    }
    this.selectedEntityEvent.emit(selectedEntity);
  }

  getRegistrationTypeToEntitySelectionText() {
    // Client's don't utilize Vendors in their registration.
    if (this.registrationType === 'Client') {
      return 'Vendor';
    } else if (this.registrationType === 'Vendor') {
      return 'Client';
    } else if (this.registrationType === 'CompanyVendor') {
      return 'Client';
    } else {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Registration Type',
        { returnError: true }
      );
    }
  }

  // The payment step carries its own state: a fee voucher or a settled invoice
  // satisfies the payment requirement outright. Resolved in one place so the
  // step's text and its color can never disagree. 'unknown' means the payment
  // record arrived without its Invoice, which callers surface as an error.
  private resolvePaymentStepState():
    'feeWaived' | 'paid' | 'expired' | 'unpaid' | 'unknown' {
    if (this.selectedCompanyVendorFeeWaived) {
      return 'feeWaived';
    }

    if (!this.selectedRecentCompanyVendorPayment) {
      return 'unpaid';
    }

    const { expStatus, Invoice } = this.selectedRecentCompanyVendorPayment;

    if (!Invoice) {
      return 'unknown';
    }

    if (expStatus === 'EXPIRED') {
      return 'expired';
    }

    const { status } = Invoice;

    return status && ['PENDING', 'PAID'].includes(status) ? 'paid' : 'unpaid';
  }

  processRegistrationStepText(RegistrationStep: RegistrationStep) {
    if (!RegistrationStep) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error adding Registration Step text to the Registration Tracker. Missing Registration step.',
        { returnError: true }
      );
    }
    const { id, name } = RegistrationStep;
    if (!id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error adding Registration Step text to the Registration Tracker. Missing Registration Step id.',
        { returnError: true }
      );
    }
    if (!name) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error adding Registration Step text to the Registration Tracker. Missing Registration step name.',
        { returnError: true }
      );
    }

    const registrationTypeSteps =
      this.stepNameToLabelMap[this.registrationType];
    if (!registrationTypeSteps) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot find registration step labels.',
        { returnError: true }
      );
    }

    const registrationStepLabel = registrationTypeSteps[name];
    if (!registrationStepLabel) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot find label for registration step.',
        { returnError: true }
      );
    }

    const textParts = [registrationStepLabel];

    if (!this.selectedEntity) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error adding Registration Step text to the Registration Tracker. Missing selected entity.',
        { returnError: true }
      );
    }
    const entityId =
      this.selectedEntity[
        this.registrationTypeToSelectionTypeMap[this.registrationType]
      ];
    if (!entityId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error adding Registration Step text to the Registration Tracker. Missing entity id.',
        { returnError: true }
      );
    }

    if (name === 'payment') {
      const paymentStepState = this.resolvePaymentStepState();

      if (paymentStepState === 'unknown') {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing invoice information.',
          { returnError: true }
        );
      }

      textParts.push(this.paymentStepStateToTextMap[paymentStepState]);

      // A fee voucher or a settled invoice satisfies the payment requirement, so
      // the step's Registration Progress cannot contradict it. The refund raised
      // to apply a fee voucher leaves that progress row on 'Failed' forever,
      // which is what showed the client 'Complete - Fee Waived - Failed'.
      if (paymentStepState === 'feeWaived' || paymentStepState === 'paid') {
        return textParts.join(' ');
      }
    }

    const RegistrationProgress =
      this.RegistrationStepToProgressMap[id][entityId];
    if (RegistrationProgress) {
      const { status } = RegistrationProgress;

      if (status) {
        textParts.push(`- ${status}`);
      }
    } else if (
      name &&
      this.forceCompleteSteps?.has(name as RegistrationStepName)
    ) {
      textParts.push('- Complete');
    }

    return textParts.join(' ');
  }

  getProgressStatusClass(
    RegistrationStep: RegistrationStep
  ): 'not-started' | 'started' | 'reviewing' | 'complete' | 'failed' {
    if (!RegistrationStep) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot apply registration progress status class. Registration step is missing.',
        { returnError: true }
      );
    }
    const { id, name } = RegistrationStep;
    if (!id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot apply registration progress status class. Missing Registration Step id.',
        { returnError: true }
      );
    }

    if (name === 'payment') {
      const paymentStepState = this.resolvePaymentStepState();

      if (paymentStepState === 'feeWaived' || paymentStepState === 'paid') {
        return 'complete';
      }
    }

    if (!this.selectedEntity) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot apply registration progress status class. Missing selected entity.',
        { returnError: true }
      );
    }
    const entityId =
      this.selectedEntity[
        this.registrationTypeToSelectionTypeMap[this.registrationType]
      ];
    if (!entityId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot apply registration progress status class. Missing entity id.',
        { returnError: true }
      );
    }

    const RegistrationProgress =
      this.RegistrationStepToProgressMap[id][entityId];

    if (RegistrationProgress) {
      const { status } = RegistrationProgress;
      const StatusToClass: {
        Started: 'started';
        Reviewing: 'reviewing';
        Complete: 'complete';
        Failed: 'failed';
      } = {
        Started: 'started',
        Reviewing: 'reviewing',
        Complete: 'complete',
        Failed: 'failed'
      };

      if (status) {
        return StatusToClass[status];
      }
    }

    if (name && this.forceCompleteSteps?.has(name as RegistrationStepName)) {
      return 'complete';
    }

    return 'not-started';
  }

  // We can update registration progress in this component when other components call this function.
  public updateRegistrationProgress(
    RegistrationProgress: RegistrationProgress
  ) {
    const { RegistrationStepId } = RegistrationProgress;
    if (!RegistrationStepId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Registration progress is missing RegistrationStepId.',
        { returnError: true }
      );
    }

    const registrationStep = this.RegistrationStepMap[RegistrationStepId];
    if (!registrationStep) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Could not find registration step to update.',
        { returnError: true }
      );
    }

    if (registrationStep.RegistrationProgresses) {
      registrationStep.RegistrationProgresses.pop();
      registrationStep.RegistrationProgresses.push(RegistrationProgress);
    } else {
      registrationStep.RegistrationProgresses = [RegistrationProgress];
    }
  }

  goToStep(RegistrationStep: RegistrationStep) {
    if (!RegistrationStep) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Registration step.',
        { returnError: true }
      );
    }

    const { name, type } = RegistrationStep;
    if (!name) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Registration step is missing property: name.',
        { returnError: true }
      );
    }
    if (!type) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Registration step is missing property: type.',
        { returnError: true }
      );
    }

    const { RegistrationProgresses } = RegistrationStep;

    if (RegistrationProgresses && RegistrationProgresses.length > 0) {
      const isValidRegistrationProgressStatus = every(
        RegistrationProgresses,
        (rp) => rp.status && ['Started', 'Complete'].includes(rp.status)
      );

      // Only allow navigation if the step is complete.
      if (isValidRegistrationProgressStatus) {
        const navigationMap = this.stepNameToNavigationMap[type];
        if (!isObject(navigationMap)) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Could not find navigation map.',
            { returnError: true }
          );
        }

        const navigationArrayFn = navigationMap[name];
        if (!isFunction(navigationArrayFn)) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Could not find navigation function.',
            { returnError: true }
          );
        }

        if (['Vendor', 'CompanyVendor'].includes(type)) {
          if (this.VendorId && (this.CompanyIds || this.BundleId)) {
            const navigationArray = navigationArrayFn(
              { CompanyIds: this.CompanyIds, BundleId: this.BundleId },
              this.VendorId
            );
            if (!isArray(navigationArray)) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Could not find navigation array parts.',
                { returnError: true }
              );
            }
            this.router.navigate(navigationArray);
          } else {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing required parameters for navigation.',
              { returnError: true }
            );
          }
        }
      }
    }
  }

  loadRegistrationStepsAndProgress() {
    if (!this.selectedEntity) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Error loading registration steps/progress. Entity not selected.',
        { returnError: true }
      );
    }
    if (!this.excludedSteps) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing excluded registration steps.',
        { returnError: true }
      );
    }

    const registrationProgressQueryObj: GenericObject = {};

    if (!isUndefined(this.CompanyVendors)) {
      Object.assign(registrationProgressQueryObj, { $or: [] });
      const { $or } = registrationProgressQueryObj;
      this.CompanyVendors.forEach((cv) => {
        const { CompanyId, hasRenewalProgress } = cv;
        // Mirrors the backend's `preferRenewalProgress` literal: renewal rows
        // are read only once they exist. `needsToRenew` is a payment-expiry
        // signal and can be true before a renewal has been started, in which
        // case there are no renewal rows and every step would render as
        // not-started even though registration is complete.
        $or.push({
          $and: [
            { CompanyId },
            { type: hasRenewalProgress ? 'renewal' : 'registration' }
          ]
        });
      });
    }
    if (!isUndefined(this.VendorId)) {
      assign(registrationProgressQueryObj, { VendorId: this.VendorId });
    }

    const queryObj = {
      name: JSON.stringify({
        $and: [
          { $in: this.registrationStepNameMap[this.registrationType] },
          { $notIn: [...Array.from(this.excludedSteps), 'signup'] }
        ]
      }),
      type: this.registrationType,
      RegistrationProgress: JSON.stringify(registrationProgressQueryObj)
    };

    this.RegistrationService.getRegistrationSteps(queryObj).subscribe({
      next: (RegistrationSteps: RegistrationStep[]) => {
        each(RegistrationSteps, (rs) => {
          const { RegistrationProgresses, id } = rs;
          if (!id) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Registration step id.',
              { returnError: true }
            );
          }

          this.RegistrationStepToProgressMap[id] = {};

          if (RegistrationProgresses) {
            if (!isArray(RegistrationProgresses)) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Registration step progress is not an array.',
                { returnError: true }
              );
            }

            if (this.registrationType === 'Vendor') {
              each(RegistrationProgresses, (rp) => {
                const { VendorId } = rp;
                if (!VendorId) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Registration Progress is missing Vendor Id.',
                    { returnError: true }
                  );
                }
                if (this.RegistrationStepToProgressMap[id]) {
                  this.RegistrationStepToProgressMap[id][VendorId] = rp;
                } else {
                  this.RegistrationStepToProgressMap[id] = {
                    [rp.VendorId!]: rp
                  };
                }
              });
            } else if (
              ['Client', 'CompanyVendor'].includes(this.registrationType)
            ) {
              each(RegistrationProgresses, (rp) => {
                const { CompanyId } = rp;
                if (!CompanyId) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Registration Progress is missing Company Id.',
                    { returnError: true }
                  );
                }
                if (this.RegistrationStepToProgressMap[id]) {
                  this.RegistrationStepToProgressMap[id][CompanyId] = rp;
                } else {
                  this.RegistrationStepToProgressMap[id] = {
                    [rp.CompanyId!]: rp
                  };
                }
              });
            } else {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error setting Registration Progress information.',
                { returnError: true }
              );
            }
          }
        });

        // Migrate sorting to the server.
        this.RegistrationSteps = sortBy(RegistrationSteps, (rs) => {
          const { name } = <
            { id: number; name: Exclude<RegistrationStepName, 'signup'> }
          >rs;
          // Will need to modify for Client registration if this component is used.
          return this.stepNameToArrayPositionMap['CompanyVendor'][name];
        });

        each(RegistrationSteps, (rs) => {
          const { id } = rs;
          this.RegistrationStepMap[id!] = rs;
        });

        // Here we need to tell other components we're done loading.
        if (this.communicator) {
          this.communicator.next('loaded');
        }
        this._cdr.markForCheck();
      }
    });
  }

  ngOnChanges(SimpleChanges: SimpleChanges): void {
    const {
      excludedSteps,
      RecentCompanyVendorPaymentsMappedByCompanyVendorId
    } = SimpleChanges;
    if (excludedSteps) {
      const { currentValue } = excludedSteps;
      if (currentValue && this.CompanyVendors.length > 0) {
        this.loadRegistrationStepsAndProgress();
      }
    }

    if (
      RecentCompanyVendorPaymentsMappedByCompanyVendorId &&
      this.selectedEntity
    ) {
      const { CompanyVendorId } = this.selectedEntity;
      if (CompanyVendorId) {
        this.selectedRecentCompanyVendorPayment =
          this.RecentCompanyVendorPaymentsMappedByCompanyVendorId.get(
            CompanyVendorId
          );
      }
    }
  }

  ngOnInit(): void {
    if (!this.registrationType) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing registration type.',
        { returnError: true }
      );
    }

    this.CompanyVendorService.getCompanyVendors({
      aliasedAttributes: JSON.stringify(['needsToRenew', 'hasRenewalProgress']),
      CompanyId: JSON.stringify({
        $in: this.CompanyIds
      }),
      Company: JSON.stringify({
        attributes: ['id', 'legalName']
      }),
      VendorId: this.VendorId || null,
      RecentCompanyVendorPayment: JSON.stringify({
        Invoice: {}
      })
    }).subscribe({
      next: (CompanyVendors: CompanyVendor[]) => {
        this.RecentCompanyVendorPaymentsMappedByCompanyVendorId.clear();
        each(CompanyVendors, (cv) => {
          const {
            id: CompanyVendorId,
            needsToRenew,
            hasRenewalProgress,
            feeWaived,
            CompanyId,
            VendorId,
            Company,
            RecentCompanyVendorPayment
          } = cv;

          if (!CompanyId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Client Id.',
              { returnError: true }
            );
          }
          if (!VendorId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Vendor Id.',
              { returnError: true }
            );
          }
          if (!CompanyVendorId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Client/Vendor Id.',
              { returnError: true }
            );
          }

          if (!feeWaived) {
            this.areAllClientVendorRelationshipsFeeWaived = false;
          }

          this.CompanyVendorFeeWaivedMappedByCompanyVendorId.set(
            CompanyVendorId,
            !!feeWaived
          );

          if (RecentCompanyVendorPayment) {
            this.RecentCompanyVendorPaymentsMappedByCompanyVendorId.set(
              CompanyVendorId,
              RecentCompanyVendorPayment
            );
          }

          if (this.registrationType === 'CompanyVendor') {
            if (!Company) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client information.',
                { returnError: true }
              );
            }
            const { legalName } = Company;
            if (!legalName) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Client is missing Legal Name.',
                { returnError: true }
              );
            }
            this.entitySelectionCollection.push({
              label: legalName,
              CompanyId,
              VendorId,
              CompanyVendorId,
              needsToRenew,
              hasRenewalProgress
            });
          }
          // Will need to handle registration type for Client if this component is reused.
          this.CompanyVendors.push(cv);
        });

        if (this.entitySelectionCollection.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Entity selections.'
          );
        }

        // Start of by selecting first entity by default. This signified which Registration Progress(es) to load.
        this.selectedEntity = this.entitySelectionCollection[0];
        this.entitySelection(this.selectedEntity);

        if (this.excludedSteps) {
          this.loadRegistrationStepsAndProgress();
        }

        if (this.communicator) {
          this.communicator.subscribe({
            next: (event: 'reload' | 'loaded') => {
              if (event === 'reload' && this.excludedSteps) {
                this.loadRegistrationStepsAndProgress();
              }
            }
          });
        }
      }
    });
  }
}
